import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../config/api';
import { User } from '../types/auth';
import { createBrowserHistory } from 'history';
import { getConnectionStatus, isUnreachableError, reportRequestOutcome } from './connectionStatus';
import { handleOfflineRequest } from './offlineAdapter';
import { isSyncingOfflineQueue } from './offlineSyncState';
import { syncClockFromServerDate } from './offlineClock';
import { cacheableRequestKey, loadOfflineResponse, offlineCacheKey, saveOfflineResponse } from './offlineCache';
import { isOfflineCajaDevice } from '../utils/serviceWorker';
import { decodeJwt } from '../utils/jwt';

const history = createBrowserHistory();

/**
 * Cierra la sesión aquí mismo, sin pasar por un diálogo.
 *
 * El intento anterior colgaba el clearCredentials del onOk de un `Modal.warning`
 * estático — y bajo React 19 ese modal no se dibuja nunca: antd 5.26 resuelve su
 * render por rc-util, que lee `render`/`createRoot` del top level de react-dom, y
 * react-dom 19 ya no los exporta (el proyecto no instala
 * @ant-design/v5-patch-for-react-19). Es la misma trampa que documenta la nota de
 * convención en TablesManager: los antd estáticos son no-ops. Resultado: el onOk
 * no corría, las credenciales muertas se quedaban y la sesión zombi sobrevivía
 * exactamente igual que antes.
 *
 * Por eso el logout es incondicional y síncrono. No hay aviso porque no hace
 * falta: la pantalla de login se explica sola, y un aviso que no se dibuja es
 * peor que ninguno. El push a /login es idempotente, así que N peticiones que
 * fallan a la vez no apilan nada.
 */
const endDeadSession = (api: { dispatch: (action: unknown) => unknown }) => {
  api.dispatch({ type: 'auth/clearCredentials' });
  if (history.location.pathname !== '/login' && history.location.pathname !== '/register') {
    history.push('/login');
  }
};

type RefreshOutcome =
  /** El servidor renovó: seguimos con tokens nuevos. */
  | { status: 'renewed'; tokens: any }
  /** 4xx: la sesión ya no sirve. Hay que desloguear. */
  | { status: 'dead' }
  /** 5xx o caída de red: el problema es del servidor, no de la sesión. Conservarla. */
  | { status: 'unavailable' };

/**
 * Un solo refresh en vuelo para toda la app.
 *
 * RefreshTokenAsync ROTA el token (`userSession.RefreshToken = newRefreshToken`).
 * Sin este candado, dos 401 simultáneos —normal en una pantalla que dispara varias
 * queries— mandaban el mismo token viejo: el primero rotaba y el segundo recibía un
 * rechazo por un token que acababa de dejar de existir. Con el logout ampliado eso
 * habría deslogueado una sesión recién renovada con éxito.
 */
let inFlightRefresh: Promise<RefreshOutcome> | null = null;

const requestTokenRefresh = async (refreshToken: string): Promise<RefreshOutcome> => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      return { status: 'renewed', tokens: await response.json() };
    }

    const body = await response.text();
    console.error('❌ Refresh rechazado:', response.status, body);

    // 5xx NO es una sesión muerta: AuthService.RefreshTokenAsync atrapa CUALQUIER
    // excepción en un Success=false, y Render devuelve 502/503 mientras redeploya.
    // Tratar eso como sesión muerta desloguearía a todas las cajeras a media
    // comida por un parpadeo de la base o un deploy. Se conserva la sesión y se
    // reintenta en la siguiente petición.
    if (response.status >= 500) {
      console.warn('⚠️ El servidor no pudo renovar (5xx) - se conserva la sesión para reintentar');
      return { status: 'unavailable' };
    }

    // 4xx sí: token vencido, revocado o inexistente. Se cubre 400 además de
    // 401/403 a propósito — un frontend nuevo contra un backend viejo (que
    // respondía 400 en todos sus fallos) tiene que desloguear igual.
    return { status: 'dead' };
  } catch (error) {
    // Fallo de RED: el token puede seguir siendo perfectamente válido.
    console.error('❌ Error de red al renovar:', error);
    return { status: 'unavailable' };
  }
};

const refreshTokenOnce = (api: { getState: () => any }): Promise<RefreshOutcome> => {
  if (!inFlightRefresh) {
    // El token se lee al construir la petición, no cuando se encoló el 401: si otro
    // refresh acaba de rotar, esta lectura ya trae el vigente.
    const token = api.getState()?.auth?.refreshToken;
    if (!token) {
      return Promise.resolve<RefreshOutcome>({ status: 'dead' });
    }
    inFlightRefresh = requestTokenRefresh(token).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

// Base query with auth token
const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as any).auth?.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Enhanced base query with automatic token refresh
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Check if this is a SESSION_EXPIRED error (session invalidated by another login)
    const errorData = result.error.data as any;
    if (errorData?.code === 'SESSION_EXPIRED') {
      console.warn('🔐 Session invalidated - another login detected');

      // Clear credentials and redirect to session expired page
      api.dispatch({ type: 'auth/clearCredentials' });
      window.location.href = '/session-expired';

      return result;
    }

    console.warn('🔒 401 Unauthorized - Token may be expired, attempting refresh...');

    // Get refresh token from state
    const state = api.getState() as any;
    const currentRefreshToken = state.auth?.refreshToken;
    const currentAccessToken = state.auth?.accessToken;

    // Only attempt refresh if we have tokens
    if (currentRefreshToken && currentAccessToken) {
      // Un refresh rechazado con 4xx ES una sesión muerta: es lo que /auth/refresh
      // contesta cuando el token venció, se revocó o ya no existe. Antes esto solo
      // reaccionaba a 401/403 mientras el backend respondía 400 en TODOS sus fallos,
      // así que el rechazo no entraba en ninguna rama: sin logout, con las
      // credenciales muertas puestas. La app se veía viva —RTK Query sigue sirviendo
      // caché— pero toda escritura moría en un 401 sin cuerpo que la UI mostraba
      // como "Error desconocido" (Cafe 45 no pudo cobrar una mesa por esto).
      const outcome = await refreshTokenOnce(api);

      if (outcome.status === 'renewed') {
        localStorage.setItem('accessToken', outcome.tokens.accessToken);
        localStorage.setItem('refreshToken', outcome.tokens.refreshToken);

        api.dispatch({ type: 'auth/setCredentials', payload: outcome.tokens });

        // Retry the original query with new token
        result = await baseQuery(args, api, extraOptions);
      } else if (outcome.status === 'dead') {
        console.warn('🔐 Refresh rechazado. Sesión expirada. Limpiando credenciales...');
        endDeadSession(api);
      }
      // 'unavailable' (5xx o red): se conserva la sesión a propósito y se reintenta
      // en la siguiente petición. El error original llega igual a quien llamó, que
      // lo traduce con getErrorMessage.
    } else {
      console.warn('❌ No tokens available, clearing session...');
      // No tokens available, clear session
      api.dispatch({ type: 'auth/clearCredentials' });
      setTimeout(() => {
        if (history.location.pathname !== '/login' && history.location.pathname !== '/register') {
          history.push('/login');
        }
      }, 100);
    }
  }

  return result;
};

/**
 * Opciones por endpoint (`extraOptions`) para las escrituras que llevan Id generado en
 * el cliente: crear orden, comandear y cobrar.
 */
export interface RetryableWriteOptions {
  /**
   * Reintentar solas cuando no se llegó al servidor (caída de red, timeout, 502-504).
   * SOLO para peticiones idempotentes: el servidor reconoce el Id repetido y contesta con
   * lo que ya guardó. En cualquier otra escritura un reintento a ciegas puede duplicarla,
   * porque un timeout no dice si el servidor alcanzó a guardar o no.
   */
  retryWhenUnreachable?: boolean;
}

/** Espera antes de cada reintento. Dos reintentos: el tercer intento sale ~3 s después del primero fallido. */
const RETRY_DELAYS_MS = [1_000, 2_000];

/**
 * Tiempo máximo de una escritura reintentable. Sin tope, una petición colgada dejaba el
 * botón de "Cobrar" girando indefinidamente —el "congelamiento" que reportan las
 * cajeras— sin que el navegador se rindiera nunca. Con el Id del cliente, rendirse y
 * reintentar es seguro aunque la primera sí se haya guardado.
 */
export const RETRYABLE_WRITE_TIMEOUT_MS = 15_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const baseQueryWithRetry: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  RetryableWriteOptions
> = async (args, api, extraOptions) => {
  let result = await baseQueryWithReauth(args, api, extraOptions);
  reportRequestOutcome(result.error);

  if (extraOptions?.retryWhenUnreachable) {
    for (const delay of RETRY_DELAYS_MS) {
      if (!isUnreachableError(result.error)) break;
      console.warn(`🔁 Sin respuesta del servidor; reintentando en ${delay} ms`, args);
      await sleep(delay);
      result = await baseQueryWithReauth(args, api, extraOptions);
      reportRequestOutcome(result.error);
    }
  }

  return result;
};

/**
 * En la caja offline (la PC con Print Agent) guarda el catálogo en IndexedDB y lo
 * sirve cuando el servidor no contesta, para que la app abra y se vea sin red. Ver
 * services/offlineCache.ts. En los demás dispositivos es transparente.
 */
const baseQueryWithOfflineCache: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  RetryableWriteOptions
> = async (args, api, extraOptions) => {
  const claims = decodeJwt((api.getState() as any).auth?.accessToken ?? '');
  const identity = claims?.userId && claims.restaurantId
    ? { userId: claims.userId, restaurantId: claims.restaurantId, userName: `${claims.firstName ?? ''} ${claims.lastName ?? ''}`.trim() }
    : null;
  const cajaOffline = isOfflineCajaDevice() && identity !== null && !isSyncingOfflineQueue();

  // Ya se sabe que no hay servidor: se atiende aquí sin gastar otro timeout de 15 s en
  // cada acción. El sondeo de /health (connectionStatus) es el que avisa cuando vuelve.
  if (cajaOffline && getConnectionStatus() === 'offline') {
    const handled = await handleOfflineRequest(args, identity!);
    if (handled) return handled;
  }

  const result = await baseQueryWithRetry(args, api, extraOptions);
  // La hora del servidor viaja en cada respuesta: con ella se corrige el reloj de la caja.
  syncClockFromServerDate((result.meta as { response?: Response } | undefined)?.response?.headers?.get('date'));

  const requestKey = isOfflineCajaDevice() && identity ? cacheableRequestKey(args) : null;
  if (requestKey && !result.error) {
    void saveOfflineResponse(offlineCacheKey(identity!.userId, identity!.restaurantId, requestKey), result.data);
    return result;
  }

  if (result.error && isUnreachableError(result.error)) {
    if (cajaOffline) {
      const handled = await handleOfflineRequest(args, identity!);
      if (handled) return handled;
    }
    if (requestKey) {
      const cached = await loadOfflineResponse(offlineCacheKey(identity!.userId, identity!.restaurantId, requestKey));
      if (cached !== undefined) return { data: cached };
    }
  }
  return result;
};

// Create the base API slice
export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithOfflineCache,
  tagTypes: [
    'Area', 'Category', 'Order', 'OrderEvent', 'PaymentMethod', 'Product', 'ProductVariant',
    'Split', 'SplitDiscount', 'SplitPayment', 'Table', 'TableEvent', 'Validation',
    // New tags for added functionality
    'Analytics', 'CashRegister', 'CashMovement', 'Receipt', 'ReceiptTemplate', 'ReceiptSequence',
    'ReceiptSettings', 'ReceiptStats', 'ReceiptShare', 'PrintJob', 'Report', 'ScheduledReport',
    'ReportTemplate', 'ReportPreset',
    // User and Restaurant management tags
    'User', 'Role', 'Restaurant', 'RestaurantSettings',
    // Printer management tags
    'PrinterConfig', 'ReceiptTemplate',
    // Shift Closing tags
    'ShiftClosing',
    // Facturacion tags
    'ClienteFiscal', 'ProductoFacturacion', 'CatalogoSAT', 'Factura', 'FacturacionConfig', 'EmailConfig',
    // App Settings tags
    'AppSettings',
    // Kitchen Display System tags
    'KitchenOrder',
    // Production Center tags
    'ProductionCenter',
    // Editor en vivo del menú digital
    'MenuEditor',
    // Customer tags
    'Customer', 'CustomerAddress',
    // Stamp balance tags
    'StampBalance',
    // SIFEI Environment (Soporte)
    'SifeiEnvironment',
    // Timbres Compartidos
    'TimbresCompartidos',
    // AI Chat
    'AiChat',
    // Inventory Module
    'InventoryCategory', 'InventoryItem', 'Supplier', 'PurchaseOrder', 'InventoryMovement', 'Recipe', 'Quotation',
    // Daily Menu Module
    'DailyMenu', 'DailyMenuTemplate', 'DailyMenuCounts',
    // Finanzas Module
    'Expense', 'ExpenseCategory', 'ExpensePaymentMethod', 'ExpenseAttachment', 'PnL',
    'AutocapturaNotice',
    // Changelog (Novedades)
    'Changelog', 'ChangelogUnseen',
    // Terminales de cobro
    'MercadoPagoConfig', 'GetnetConfig',
    // Ingesta de menú con IA
    'MenuIngest',
    // Checklist de primeros pasos (calculado desde el tenant)
    'Onboarding'
  ],
  endpoints: (builder) => ({
    getCurrentUser: builder.query<User, void>({
      query: () => '/auth/me',
    }),
    validateToken: builder.mutation<{ isValid: boolean }, string>({
      query: (token) => ({
        url: '/auth/validate',
        method: 'POST',
        body: { token },
      }),
    }),
  }),
});

export const { useGetCurrentUserQuery, useValidateTokenMutation } = baseApi;

export default baseApi;